import React, { useMemo } from 'react';
import { ClipboardList } from 'lucide-react';

type DraftV1 = {
  version?: number;
  profile?: Record<string, unknown>;
  entity?: Record<string, unknown>;
  ownership?: Record<string, unknown>;
};

const INCOME_SOURCE_LABELS: Record<string, string> = {
  sales_goods: 'Sales of goods / products',
  service_fees: 'Service fees / professional services',
  subscription: 'Subscription / recurring revenue',
  investment_trading: 'Investment / trading income',
  rental_leasing: 'Rental / leasing',
  parent_shareholder_funding: 'Funding from parent company / shareholders',
  loans_credit: 'Loans / credit',
  other: 'Other (specify)',
};

/** Same order / labels as portal `BusinessOnboardingWizardPage` entity step */
const ENTITY_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['incorporationCountry', 'Country of incorporation'],
  ['legalEntityName', 'Legal entity name'],
  ['registrationNumber', 'Registration number'],
  ['taxId', 'Tax ID'],
  ['regAddressLine', 'Registered address — street'],
  ['regCity', 'City'],
  ['regPostal', 'Postal code'],
  ['regCountry', 'Country (registered office)'],
  ['officeSameAsReg', 'Office address matches registered address'],
  ['officeAddressLine', 'Office address — street'],
  ['phone', 'Phone'],
  ['tradingNames', 'Trading name(s)'],
  ['website', 'Website'],
  ['mainIndustry', 'Main industry'],
  ['additionalIndustry', 'Additional industry'],
  ['incomingCountries', 'Incoming funds from'],
  ['outgoingCountries', 'Outgoing funds to'],
  ['incomeSource', 'Primary source of income / wealth'],
  ['incomeSourceOther', 'Specify income source'],
  ['productsDescription', 'Products / services description'],
];

const KNOWN_ENTITY_KEYS = new Set<string>([
  'entityType',
  'entityTypeOther',
  ...ENTITY_PAIRS.map(([k]) => k),
]);

const STAKEHOLDER_FIELD_LABELS: Record<string, string> = {
  partyType: 'Type',
  role: 'Role',
  firstName: 'Legal first name',
  lastName: 'Legal last name',
  middleName: 'Legal middle name',
  dateOfBirth: 'Date of birth',
  taxCountry: 'Tax country',
  taxIdPersonal: 'Personal tax ID',
  residenceCountry: 'Residence country',
  residenceAddress: 'Residence address',
  identityType: 'Identity type',
  identityDocumentNumber: 'Identity document number',
  identityIssueDate: 'Issue date',
  identityExpiryDate: 'Expiry date',
  identityExpiryIndefinite: 'No expiry date (lifetime)',
  percentShares: '% of shares',
  alsoDirector: 'Also director (shareholder-director linkage)',
};

function formatJsonPretty(raw: string | null | undefined): string {
  const s = (raw ?? '').trim();
  if (!s) return '—';
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return raw ?? '—';
  }
}

function displayScalar(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return String(v);
  const s = String(v).trim();
  return s || '—';
}

function entityTypeLabel(raw: string): string {
  const spaced = raw.replace(/_/g, ' ').trim();
  if (!spaced) return raw;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

function incomeSourceLabel(code: string): string {
  const k = code.trim();
  return (INCOME_SOURCE_LABELS[k] ?? k) || '—';
}

function roleLabel(role: string): string {
  return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function DlItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 max-w-full min-w-0 break-words text-sm text-gray-900 whitespace-pre-wrap [overflow-wrap:anywhere]">
        {children}
      </dd>
    </div>
  );
}

function parseDraft(raw: string | null | undefined): DraftV1 | null {
  const s = (raw ?? '').trim();
  if (!s) return null;
  try {
    return JSON.parse(s) as DraftV1;
  } catch {
    return null;
  }
}

function renderStakeholderCard(row: Record<string, unknown>, title: string) {
  const entries = Object.entries(row).filter(([k]) => k !== 'syncedLinks');
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-4 text-sm">
      <p className="mb-3 font-semibold text-gray-900">{title}</p>
      <dl className="space-y-2">
        {entries.map(([key, val]) => {
          if (val === undefined || val === null || val === '') return null;
          if (typeof val === 'object') return null;
          const lab = STAKEHOLDER_FIELD_LABELS[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
          const shown = key === 'role' ? roleLabel(String(val)) : displayScalar(val);
          return <DlItem key={key} label={lab}>{shown}</DlItem>;
        })}
      </dl>
    </div>
  );
}

export type PortalClientDataReadViewProps = {
  applicationType?: string | null;
  extendedQuestionnaireJson?: string | null;
  directorVerificationJson?: string | null;
};

export function PortalClientDataReadView({
  applicationType,
  extendedQuestionnaireJson,
  directorVerificationJson,
}: PortalClientDataReadViewProps) {
  const isBusiness = (applicationType ?? '').toUpperCase() === 'BUSINESS';

  const draft = useMemo(() => parseDraft(extendedQuestionnaireJson), [extendedQuestionnaireJson]);

  const directorParsed = useMemo(() => {
    const s = (directorVerificationJson ?? '').trim();
    if (!s) return null;
    try {
      return JSON.parse(s) as unknown;
    } catch {
      return null;
    }
  }, [directorVerificationJson]);

  const hasExtended = Boolean((extendedQuestionnaireJson ?? '').trim());
  const hasDirector = Boolean((directorVerificationJson ?? '').trim());

  if (!isBusiness && !hasExtended && !hasDirector) {
    return (
      <section className="min-w-0 rounded-xl border border-gray-200 bg-white p-5 shadow-sm lg:col-span-2">
        <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
          <ClipboardList className="h-5 w-5 text-gray-500" />
          <h2 className="font-semibold text-gray-900">Client portal questionnaire</h2>
        </div>
        <p className="mt-4 text-sm text-gray-600">
          For <strong>Individual</strong> applications the client fills company and people in the simpler wizard — data
          is shown in <strong>Company</strong>, <strong>Persons</strong>, and <strong>Documents</strong> above/below.
          There is no extended JSON draft for this type.
        </p>
      </section>
    );
  }

  const isV1 = draft && draft.version === 1;
  const profile = (isV1 && draft.profile && typeof draft.profile === 'object' ? draft.profile : {}) as Record<
    string,
    unknown
  >;
  const entity = (isV1 && draft.entity && typeof draft.entity === 'object' ? draft.entity : {}) as Record<
    string,
    unknown
  >;
  const ownership = (isV1 && draft.ownership && typeof draft.ownership === 'object' ? draft.ownership : {}) as Record<
    string,
    unknown
  >;

  const interests = Array.isArray(profile.interests) ? (profile.interests as unknown[]).filter((x) => String(x).trim()) : [];

  const shareholderRows = Array.isArray(ownership.shareholderRows)
    ? (ownership.shareholderRows as Record<string, unknown>[])
    : [];
  const extraDirectorRows = Array.isArray(ownership.extraDirectorRows)
    ? (ownership.extraDirectorRows as Record<string, unknown>[])
    : [];

  return (
    <section className="min-w-0 rounded-xl border border-gray-200 bg-white p-5 shadow-sm lg:col-span-2">
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 pb-3">
        <ClipboardList className="h-5 w-5 text-gray-500" />
        <h2 className="font-semibold text-gray-900">Client portal — submitted questionnaire</h2>
      </div>
      <p className="mt-3 text-sm text-gray-600">
        Mirrors what the client enters in the portal (business extended questionnaire). Company block elsewhere may overlap
        legal name synced to compliance.
      </p>

      {isBusiness && !hasExtended ? (
        <p className="mt-4 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-600">
          Extended questionnaire JSON is empty — questionnaire answers appear after the client saves steps in the business
          onboarding wizard (or stays empty until their first save).
        </p>
      ) : null}

      {!isV1 && hasExtended ? (
        <div className="mt-4 rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="font-medium">Unrecognised questionnaire shape</p>
          <p className="mt-1 text-amber-900/90">Showing structured fields where possible and raw JSON below.</p>
        </div>
      ) : null}

      {isV1 ? (
        <div className="mt-6 space-y-8">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Profile & expected activity</h3>
            <dl className="mt-3 grid gap-3 sm:grid-cols-2">
              <DlItem label="Company size (employees band)">{displayScalar(profile.employeeBand)}</DlItem>
              <DlItem label="Expected monthly volume (USD)">{displayScalar(profile.monthlyUsdVolumeBand)}</DlItem>
              <DlItem label="Incoming transactions / month">{displayScalar(profile.incomingTxBand)}</DlItem>
              <DlItem label="Outgoing transactions / month">{displayScalar(profile.outgoingTxBand)}</DlItem>
              <div className="sm:col-span-2">
                <DlItem label="Areas of interest">
                  {interests.length ? (
                    <ul className="list-disc pl-5">
                      {interests.map((t) => (
                        <li key={String(t)}>{String(t)}</li>
                      ))}
                    </ul>
                  ) : (
                    '—'
                  )}
                </DlItem>
              </div>
            </dl>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900">Entity & business description</h3>
            <dl className="mt-3 grid gap-3 sm:grid-cols-2">
              {typeof entity.entityType === 'string' && entity.entityType.trim() ? (
                <DlItem label="Entity type">{entityTypeLabel(entity.entityType)}</DlItem>
              ) : null}
              {typeof entity.entityTypeOther === 'string' && entity.entityTypeOther.trim() ? (
                <DlItem label="Specify entity type">{entity.entityTypeOther}</DlItem>
              ) : null}
              {ENTITY_PAIRS.map(([key, lab]) => {
                if (!(key in entity)) return null;
                if (['entityType', 'entityTypeOther'].includes(key)) return null;
                const val = entity[key];
                let text: React.ReactNode;
                if (key === 'incomeSource') {
                  text = incomeSourceLabel(String(val ?? ''));
                } else if (key === 'officeSameAsReg') {
                  text = displayScalar(val);
                } else if (typeof val === 'boolean') {
                  text = displayScalar(val);
                } else {
                  text = displayScalar(val);
                }
                if (text === '—' && key !== 'officeSameAsReg') return null;
                return (
                  <div key={key} className={key === 'productsDescription' ? 'sm:col-span-2' : undefined}>
                    <DlItem label={lab}>{text}</DlItem>
                  </div>
                );
              })}
            </dl>
            {/* Any extra keys client saved (forward compatibility) */}
            {(() => {
              const extra = Object.keys(entity).filter((k) => !KNOWN_ENTITY_KEYS.has(k));
              if (!extra.length) return null;
              return (
                <div className="mt-4 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase text-gray-500">Other entity fields</p>
                  <dl className="mt-2 grid gap-2 sm:grid-cols-2">
                    {extra.map((k) => (
                      <DlItem key={k} label={k}>
                        {displayScalar(entity[k])}
                      </DlItem>
                    ))}
                  </dl>
                </div>
              );
            })()}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900">Ownership & governance (questionnaire)</h3>
            <dl className="mt-3 grid gap-3 sm:grid-cols-2">
              <DlItem label="Nominee / third party (shares on behalf of another)">{displayScalar(ownership.nomineeThirdPartyAnswer)}</DlItem>
              {String(ownership.nomineeThirdPartyAnswer ?? '').toLowerCase() === 'yes' ? (
                <div className="sm:col-span-2">
                  <DlItem label="Nominee / third-party details">{displayScalar(ownership.nomineeQuestion)}</DlItem>
                </div>
              ) : null}
              <DlItem label="Shareholders are directors">{displayScalar(ownership.shareholdersAreDirectors)}</DlItem>
              <DlItem label="Director coverage">{displayScalar(ownership.shareholdersDirectorCoverage)}</DlItem>
            </dl>
            {shareholderRows.filter((r) => r && typeof r === 'object').length ? (
              <div className="mt-4 space-y-3">
                <p className="text-xs font-semibold uppercase text-gray-500">Shareholders</p>
                <div className="grid gap-3 md:grid-cols-2">
                  {shareholderRows.map((row, ix) => (
                    <React.Fragment key={`shr-${ix}`}>{renderStakeholderCard(row, `Shareholder ${ix + 1}`)}</React.Fragment>
                  ))}
                </div>
              </div>
            ) : null}
            {extraDirectorRows.filter((r) => r && typeof r === 'object').length ? (
              <div className="mt-4 space-y-3">
                <p className="text-xs font-semibold uppercase text-gray-500">Additional directors (not shareholders)</p>
                <div className="grid gap-3 md:grid-cols-2">
                  {extraDirectorRows.map((row, ix) => (
                    <React.Fragment key={`extra-dir-${ix}`}>
                      {renderStakeholderCard(row, `Additional director ${ix + 1}`)}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        hasExtended && (
          <pre className="mt-4 max-h-[28rem] overflow-auto whitespace-pre-wrap break-all rounded-lg bg-gray-50 p-4 text-xs text-gray-800">
            {formatJsonPretty(extendedQuestionnaireJson)}
          </pre>
        )
      )}

      {directorParsed !== null ? (
        <div className="mt-8 border-t border-gray-100 pt-6">
          <h3 className="text-sm font-semibold text-gray-900">Director verification (structured)</h3>
          {typeof directorParsed === 'object' && directorParsed !== null && !Array.isArray(directorParsed) ? (
            <dl className="mt-3 grid gap-3 sm:grid-cols-2">
              {Object.entries(directorParsed as Record<string, unknown>).map(([k, v]) => (
                <DlItem key={k} label={k.replace(/_/g, ' ')}>
                  {typeof v === 'object' && v !== null ? (
                    <pre className="mt-1 max-h-40 overflow-auto rounded bg-gray-50 p-2 text-xs">
                      {JSON.stringify(v, null, 2)}
                    </pre>
                  ) : (
                    displayScalar(v)
                  )}
                </DlItem>
              ))}
            </dl>
          ) : (
            <p className="mt-2 text-sm text-gray-700">{displayScalar(directorParsed as unknown)}</p>
          )}
        </div>
      ) : null}

      {(hasExtended || hasDirector) && (
        <details className="mt-8 rounded-lg border border-gray-100 bg-gray-50/80 p-4 text-sm">
          <summary className="cursor-pointer font-medium text-gray-800">Raw JSON (technical)</summary>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            {hasExtended ? (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase text-gray-500">extendedQuestionnaireJson</p>
                <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-white p-3 text-xs text-gray-800">
                  {formatJsonPretty(extendedQuestionnaireJson)}
                </pre>
              </div>
            ) : null}
            {hasDirector ? (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase text-gray-500">directorVerificationJson</p>
                <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-white p-3 text-xs text-gray-800">
                  {formatJsonPretty(directorVerificationJson)}
                </pre>
              </div>
            ) : null}
          </div>
        </details>
      )}
    </section>
  );
}
